import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { Route, Routes } from "react-router";
import { MotionConfig, motion } from "framer-motion";
import AmbientBackground from "./components/letslearning/AmbientBackground";
import Nav, { type Theme } from "./components/letslearning/Nav";
import LiquidGlassFilter from "./components/letslearning/LiquidGlassFilter";
import Footer from "./components/letslearning/Footer";
import ContentProvider from "./content/ContentProvider";
import { useContentState } from "./content/ContentContext";

// 首页是首要入口：在根路径尽早请求代码块，同时避免文章直达时下载首页代码。
const initialHome = window.location.pathname === "/" ? import("./pages/Home") : null;
const Home = lazy(() => initialHome ?? import("./pages/Home"));
const ArticlesPage = lazy(() => import("./pages/ArticlesPage"));
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const SearchOverlay = lazy(() => import("./components/letslearning/SearchOverlay"));

function LoadingScreen() {
  return (
    <div className="flex min-h-[100svh] flex-col items-center justify-center gap-5">
      <motion.span
        className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#fb7299] via-[#e05a86] to-[#7d7aff] text-[22px] font-bold text-white shadow-[0_0_40px_rgba(251,114,153,0.4)]"
        animate={{ scale: [1, 1.06, 1], opacity: [0.85, 1, 0.85] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
      >
        L
      </motion.span>
      <motion.span
        className="t-50 text-[13px] font-medium tracking-[0.2em]"
        animate={{ opacity: [0.55, 1, 0.55] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
      >
        正在加载内容…
      </motion.span>
    </div>
  );
}

function ErrorScreen({ message }: { message: string }) {
  return (
    <div className="flex min-h-[100svh] flex-col items-center justify-center gap-4 px-6 text-center">
      <h1 className="headline-gradient text-3xl font-semibold tracking-tight">内容加载失败</h1>
      <p className="t-60 max-w-md text-[14px] leading-relaxed">
        {message}。请检查网络后刷新重试；若持续失败，可能是内容文件缺失。
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="glass-chip mt-2 flex h-11 items-center rounded-full px-6 text-[14px] font-medium t-85 transition-transform duration-500 hover:scale-[1.03] active:scale-[0.97]"
      >
        重新加载
      </button>
    </div>
  );
}

function Shell() {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      const saved = localStorage.getItem("ll-theme");
      return saved === "light" || saved === "neu" ? saved : "dark";
    } catch {
      return "dark";
    }
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchUsed, setSearchUsed] = useState(false);
  const { content, error } = useContentState();

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("light", theme === "light");
    root.classList.toggle("neu", theme === "neu");
    root.style.colorScheme = theme === "dark" ? "dark" : "light";
    document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute(
      "content",
      theme === "dark" ? "#050508" : theme === "neu" ? "#e0e5ec" : "#f5f6fa"
    );
    try {
      localStorage.setItem("ll-theme", theme);
    } catch {
      // 禁用站点存储时主题仍可在当前会话使用。
    }
  }, [theme]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchUsed(true);
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const openSearch = useCallback(() => {
    setSearchUsed(true);
    setSearchOpen(true);
  }, []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);

  return (
    <div className="relative min-h-screen">
      <AmbientBackground theme={theme} />
      <Nav theme={theme} onThemeChange={setTheme} onOpenSearch={openSearch} />
      <main>
        {error ? (
          <ErrorScreen message={error} />
        ) : !content ? (
          <LoadingScreen />
        ) : (
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/articles" element={<ArticlesPage />} />
              <Route path="/articles/:slug" element={<ArticlePage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        )}
      </main>
      {content && <Footer />}
      {content && searchUsed && (
        <Suspense fallback={null}>
          <SearchOverlay open={searchOpen} onClose={closeSearch} />
        </Suspense>
      )}
      <LiquidGlassFilter />
    </div>
  );
}

export default function App() {
  return (
    <MotionConfig reducedMotion="user">
      <ContentProvider>
        <Shell />
      </ContentProvider>
    </MotionConfig>
  );
}
